import React, { useState } from 'react'
import {
  Alert,
  Box,
  Typography,
  Paper,
  TextField,
  MenuItem,
  Button,
  Link,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material'
import { FlowIdView } from './JobView'
import { startPdfTreeTranslateDiscovery } from './utility'
import { FlowResult, PdfTranslationResultJob } from './types'
import { contentServicePost } from 'infrastructure/contentService'
import { PdfTreeTranslateDiscoveryJob } from './types'
import { TreeIdInput } from '../tree/navigationHelpers/TreeNodeRouter'

interface PdfTranslateResult extends FlowResult {
  data: {
    blocks_extracted: number
    blocks_translated: number
    original_url: string
    overflow_blocks: unknown[]
    pages: number
    telemetry: {
      completion_tokens: number
      prompt_tokens: number
      total_tokens: number
    }
    translated_url: string
  }
  meta: {
    controller_id: string
    create_time: number
    expire_time: number
    job_type: string
    pdf_url: string
    prompt_id: string
    worker_type: 'pdf_translate'
  }
}

type PdfToolMode = 'translate' | 'tree-discovery' | 'tree-translate'

function isHttpUrl(url: string): boolean {
  return /^https?:\/\/.+/i.test(url.trim())
}

function looksLikePdf(url: string): boolean {
  try {
    const pathname = new URL(url.trim()).pathname
    return pathname.toLowerCase().endsWith('.pdf')
  } catch {
    return false
  }
}

export function PdfTranslate() {
  const [mode, setMode] = useState<PdfToolMode>('translate')
  const [pdfUrl, setPdfUrl] = useState('')
  const [promptId, setPromptId] = useState('spanish_translation_k5')
  const [translateFlowIds, setTranslateFlowIds] = useState<string[]>([])
  const [translateStartError, setTranslateStartError] = useState<string | null>(
    null,
  )
  const [isStartingTranslate, setIsStartingTranslate] = useState(false)
  const [selectedTree, setSelectedTree] = useState<{
    treeId: string
    versionId: string
    title: string
  }>()
  const [discoveryFlowIds, setDiscoveryFlowIds] = useState<string[]>([])
  const [discoveryStartError, setDiscoveryStartError] = useState<string | null>(
    null,
  )
  const [isStartingDiscovery, setIsStartingDiscovery] = useState(false)
  const [treeTranslateTree, setTreeTranslateTree] = useState<{
    treeId: string
    versionId: string
    title: string
  }>()
  const [treeTranslatePromptId, setTreeTranslatePromptId] = useState(
    'spanish_translation_k5',
  )
  const [treeTranslateFlowIds, setTreeTranslateFlowIds] = useState<string[]>([])
  const [treeTranslateStartError, setTreeTranslateStartError] = useState<
    string | null
  >(null)
  const [isStartingTreeTranslate, setIsStartingTreeTranslate] = useState(false)
  const canStartTreeTranslate =
    Boolean(treeTranslateTree) && !isStartingTreeTranslate
  const isValidHttp = isHttpUrl(pdfUrl)
  const isPdfExtension = isValidHttp && looksLikePdf(pdfUrl)
  const canStartTranslate = isValidHttp && !isStartingTranslate
  const canStartDiscovery = Boolean(selectedTree) && !isStartingDiscovery

  const handleStartTranslate = async () => {
    setTranslateStartError(null)
    setIsStartingTranslate(true)
    try {
      const { result, error } = await contentServicePost<{
        data: { flow_id: string }
      }>({
        path: '/content/v1/ai-tree-tool/pdf/translate',
        body: {
          pdf_url: pdfUrl,
          area: 'translation',
          prompt_id: promptId,
          model: 'openai/gpt-4.1-mini',
          provider: 'openrouter',
        },
      })

      if (error || !result?.data?.flow_id) {
        setTranslateStartError(
          error
            ? `PDF translate start failed: ${error}`
            : 'No flow ID received from API',
        )
        return
      }

      setTranslateFlowIds((prev) => [result.data.flow_id, ...prev])
    } catch (err) {
      setTranslateStartError(
        `Failed to start: ${err instanceof Error ? err.message : String(err)}`,
      )
    } finally {
      setIsStartingTranslate(false)
    }
  }

  const handleStartDiscovery = async () => {
    setDiscoveryStartError(null)
    setIsStartingDiscovery(true)

    try {
      if (!selectedTree) {
        setDiscoveryStartError('Select a tree before starting discovery.')
        return
      }

      const flowId = await startPdfTreeTranslateDiscovery({
        tree_id: selectedTree.treeId,
        version_id: selectedTree.versionId,
      })
      setDiscoveryFlowIds((prev) => [flowId, ...prev])
    } catch (err) {
      setDiscoveryStartError(
        `Failed to start: ${err instanceof Error ? err.message : String(err)}`,
      )
    } finally {
      setIsStartingDiscovery(false)
    }
  }

  const handleStartTreeTranslate = async () => {
    setTreeTranslateStartError(null)
    setIsStartingTreeTranslate(true)
    try {
      if (!treeTranslateTree) {
        setTreeTranslateStartError(
          'Select a tree before starting tree translation.',
        )
        return
      }

      const { result, error } = await contentServicePost<{
        data: { flow_id: string }
      }>({
        path: `/content/v1/pdf-tree-translate/${treeTranslateTree.treeId}:${treeTranslateTree.versionId}/start`,
        body: {
          prompt_id: treeTranslatePromptId,
          model: 'openai/gpt-4.1-mini',
          area: 'translation',
          provider: 'openrouter',
          timeout: 120,
          force: false,
        },
      })

      if (error || !result?.data?.flow_id) {
        setTreeTranslateStartError(
          error
            ? `Tree translation start failed: ${error}`
            : 'No flow ID received from API',
        )
        return
      }

      setTreeTranslateFlowIds((prev) => [result.data.flow_id, ...prev])
    } catch (err) {
      setTreeTranslateStartError(
        `Failed to start: ${err instanceof Error ? err.message : String(err)}`,
      )
    } finally {
      setIsStartingTreeTranslate(false)
    }
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        PDF Tools
      </Typography>

      <Typography variant="body1" paragraph>
        Start Hermes jobs for either direct PDF translation or tree-level PDF
        discovery.
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={mode}
          onChange={(_event, value: PdfToolMode) => setMode(value)}
          aria-label="pdf tools tabs"
        >
          <Tab label="Translate PDF" value="translate" />
          <Tab label="Discover PDFs in Tree" value="tree-discovery" />
          <Tab label="Translate Tree PDFs" value="tree-translate" />
        </Tabs>
      </Box>

      {mode === 'translate' && (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <TextField
              label="PDF URL"
              value={pdfUrl}
              onChange={(e) => {
                setPdfUrl(e.target.value)
                setTranslateStartError(null)
              }}
              placeholder="https://example.com/document.pdf"
              variant="outlined"
              fullWidth
              sx={{ mb: 2 }}
            />

            <TextField
              select
              label="Translation Prompt"
              value={promptId}
              onChange={(e) => setPromptId(e.target.value)}
              variant="outlined"
              fullWidth
              sx={{ mb: 2 }}
            >
              <MenuItem value="spanish_translation_k5">
                Spanish Translation K–5
              </MenuItem>
              <MenuItem value="spanish_translation_6_12">
                Spanish Translation 6–12
              </MenuItem>
            </TextField>

            <Button
              variant="contained"
              onClick={handleStartTranslate}
              disabled={!canStartTranslate}
            >
              {isStartingTranslate ? 'Starting...' : 'Start Translation'}
            </Button>

            {isValidHttp && !isPdfExtension && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                URL does not end in .pdf — make sure this points to a valid PDF
                file.
              </Alert>
            )}

            {translateStartError && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {translateStartError}
              </Typography>
            )}
          </Paper>

          {translateFlowIds.map((flowId) => (
            <Paper key={flowId} sx={{ p: 3, mb: 2 }}>
              <FlowIdView<PdfTranslateResult>
                flowId={flowId}
                FlowResultComponent={PdfTranslateFlowResult}
              />
            </Paper>
          ))}
        </>
      )}

      {mode === 'tree-discovery' && (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <TreeIdInput
              cta="Use Tree"
              onChange={({ treeId, versionId, title }) => {
                setSelectedTree({ treeId, versionId, title })
                setDiscoveryStartError(null)
              }}
            />

            {selectedTree && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Selected tree: {selectedTree.title} ({selectedTree.treeId}:
                  {selectedTree.versionId})
                </Typography>
              </Box>
            )}

            <Button
              variant="contained"
              onClick={handleStartDiscovery}
              disabled={!canStartDiscovery}
            >
              {isStartingDiscovery ? 'Starting...' : 'Start Tree PDF Discovery'}
            </Button>

            {discoveryStartError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {discoveryStartError}
              </Alert>
            )}
          </Paper>

          {discoveryFlowIds.map((flowId) => (
            <Paper key={flowId} sx={{ p: 3, mb: 2 }}>
              <FlowIdView<PdfTreeTranslateDiscoveryJob> flowId={flowId} />
            </Paper>
          ))}
        </>
      )}

      {mode === 'tree-translate' && (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <TreeIdInput
              cta="Use Tree"
              onChange={({ treeId, versionId, title }) => {
                setTreeTranslateTree({ treeId, versionId, title })
                setTreeTranslateStartError(null)
              }}
            />

            {treeTranslateTree && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Selected tree: {treeTranslateTree.title} (
                  {treeTranslateTree.treeId}:{treeTranslateTree.versionId})
                </Typography>
              </Box>
            )}

            <TextField
              select
              label="Translation Prompt"
              value={treeTranslatePromptId}
              onChange={(e) => setTreeTranslatePromptId(e.target.value)}
              variant="outlined"
              fullWidth
              sx={{ mt: 2, mb: 2 }}
            >
              <MenuItem value="spanish_translation_k5">
                Spanish Translation K-5
              </MenuItem>
              <MenuItem value="spanish_translation_6_12">
                Spanish Translation 6-12
              </MenuItem>
            </TextField>

            <Button
              variant="contained"
              onClick={handleStartTreeTranslate}
              disabled={!canStartTreeTranslate}
            >
              {isStartingTreeTranslate
                ? 'Starting...'
                : 'Start Tree PDF Translation'}
            </Button>

            {treeTranslateStartError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {treeTranslateStartError}
              </Alert>
            )}
          </Paper>

          {treeTranslateFlowIds.map((flowId) => (
            <Paper key={flowId} sx={{ p: 3, mb: 2 }}>
              <FlowIdView<PdfTranslationResultJob> flowId={flowId} />
            </Paper>
          ))}
        </>
      )}
    </Box>
  )
}

function PdfTranslateFlowResult({
  flowResult,
}: {
  flowResult: PdfTranslateResult | undefined
}) {
  if (!flowResult?.data) return null

  const { data, meta } = flowResult

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Translation Complete
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Link
          href={data.translated_url}
          target="_blank"
          rel="noopener noreferrer"
          variant="h6"
        >
          Open Translated PDF
        </Link>
      </Box>

      <Typography variant="subtitle1" gutterBottom>
        Stats
      </Typography>
      <Table size="small" sx={{ mb: 3, maxWidth: 400 }}>
        <TableBody>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>Pages</TableCell>
            <TableCell>{data.pages}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>Blocks Extracted</TableCell>
            <TableCell>{data.blocks_extracted}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>Blocks Translated</TableCell>
            <TableCell>{data.blocks_translated}</TableCell>
          </TableRow>
          {data.overflow_blocks?.length && (
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Overflow Blocks</TableCell>
              <TableCell>{data.overflow_blocks.length}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {data.telemetry && (
        <>
          <Typography variant="subtitle1" gutterBottom>
            Token Usage
          </Typography>
          <Table size="small" sx={{ mb: 3, maxWidth: 400 }}>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Prompt Tokens</TableCell>
                <TableCell>
                  {data.telemetry.prompt_tokens.toLocaleString()}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  Completion Tokens
                </TableCell>
                <TableCell>
                  {data.telemetry.completion_tokens.toLocaleString()}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Total Tokens</TableCell>
                <TableCell>
                  {data.telemetry.total_tokens.toLocaleString()}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </>
      )}

      <Typography variant="subtitle1" gutterBottom>
        Job Details
      </Typography>
      <Table size="small" sx={{ maxWidth: 600 }}>
        <TableBody>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>Prompt ID</TableCell>
            <TableCell>{meta.prompt_id}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>Worker Type</TableCell>
            <TableCell>{meta.worker_type}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>Original PDF</TableCell>
            <TableCell>
              <Link
                href={data.original_url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ wordBreak: 'break-all' }}
              >
                {data.original_url}
              </Link>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  )
}
